import React from 'react';
import { RotateCcw, Trash2, X } from 'lucide-react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import {
  badgeStyle,
  buttonBase,
  dangerButton,
  modalBackdropStyle,
  modalHeaderStyle,
  modalStyle,
  primaryButton,
  summaryCardStyle,
} from './styles';
import {
  stdModalHeaderIconBox, stdModalHeaderTitle, stdModalHeaderSub, stdModalCloseBtn,
} from '../adminlayout/designStandard';

export const STATUS_OPTIONS = [
  ['all', '전체'],
  ['probation', '수습검토'],
  ['contract_due', '계약임박'],
  ['ok', '여유'],
  ['missing', '확인필요'],
  ['resigned', '퇴사'],
];

export function formatDday(status) {
  if (!status || status.dday === null || status.dday === undefined) return '-';
  if (status.dday < 0) return `${Math.abs(status.dday)}일 지남`;
  if (status.dday === 0) return '오늘';
  return `${status.dday}일 남음`;
}

export function probationText(term) {
  if (!term?.probation_label) return '';
  return `수습 ${term.probation_label}${term.probation_end_date ? ` · ${term.probation_end_date}까지` : ''}`;
}

export function Summary({ label, value, tone }) {
  return (
    <div style={summaryCardStyle}>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', fontWeight: 800 }}>{label}</div>
      <div style={{ marginTop: 5, fontSize: 21, fontWeight: 950, color: tone || 'var(--text)' }}>{value}</div>
    </div>
  );
}

export function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 800 }}>{label}</div>
      <div style={{ marginTop: 2, color: 'var(--text)', fontWeight: 850 }}>{value}</div>
    </div>
  );
}

export function NextCheckCell({ row }) {
  const next = row.next_check || {};
  const mainLabel = next.label || row.status?.label || '-';
  const date = next.date || row.current_term?.end_date || '-';
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={badgeStyle(next.tone || row.status?.tone)}>{mainLabel}</span>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>{formatDday(next)}</strong>
      </div>
      <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 850 }}>{date}</div>
      {next.secondary && (
        <div style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 750 }}>{next.secondary}</div>
      )}
    </div>
  );
}

export function ActionButtons({ row, saving, onDetail, onRenew, onEnd, onDelete }) {
  const ended = row.current_term?.decision_status === 'ended' || row.resign_date;
  const canDelete = row.enrollment?.registration_type === 'manual'
    && Number(row.term_count || 0) <= 1
    && row.current_term?.decision_status === 'pending'
    && row.current_term?.source === 'manual_register';
  return (
    <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      <button type="button" style={buttonBase} onClick={() => onDetail(row)}>상세</button>
      {canDelete && (
        <button type="button" style={dangerButton(saving)} disabled={saving} onClick={() => onDelete(row)}>
          <Trash2 size={13} /> 삭제
        </button>
      )}
      <button type="button" style={primaryButton(saving)} disabled={saving || ended} onClick={() => onRenew(row)}>
        갱신
      </button>
      <button type="button" style={dangerButton(saving)} disabled={saving || ended} onClick={() => onEnd(row)}>
        종료
      </button>
    </div>
  );
}

export function MobileCard({ row, saving, handlers }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--bg-card)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 950, color: 'var(--text)' }}>{row.name}</div>
          <div style={{ marginTop: 3, fontSize: 12, color: 'var(--text-sub)', fontWeight: 700 }}>
            {row.department || '-'} · 입사 {row.hire_date || '-'}
          </div>
        </div>
        <span style={badgeStyle(row.status.tone)}>{row.status.label}</span>
      </div>
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Info label="다음 확인" value={<NextCheckCell row={row} />} />
        </div>
        <Info label="계약 만료일" value={row.current_term?.end_date || '-'} />
        <Info label="현재 구간" value={row.current_term?.term_label || '-'} />
        <Info label="결정" value={row.current_term?.decision_label || '-'} />
      </div>
      <div style={{ marginTop: 10 }}>
        <ActionButtons row={row} saving={saving} {...handlers} />
      </div>
    </div>
  );
}

export function DetailModal({ detail, memoDrafts, saving, onClose, onMemo, onSaveMemo, onRenew, onEnd, onUndo }) {
  useBodyScrollLock(Boolean(detail));

  if (!detail) return null;
  const user = detail.user || {};
  const enrollment = detail.enrollment || {};
  const terms = detail.terms || [];
  return (
    <div style={modalBackdropStyle} onMouseDown={onClose}>
      <div style={modalStyle} onMouseDown={(event) => event.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>📋</div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ ...stdModalHeaderTitle, margin: 0 }}>{user.name} 계약갱신 이력</h3>
              <div style={stdModalHeaderSub}>{user.department || '-'} · 입사 {user.hire_date || '-'} · 퇴사 {user.resign_date || '-'}</div>
            </div>
          </div>
          <button type="button" style={stdModalCloseBtn} onClick={onClose}><X size={15} /></button>
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" style={buttonBase} disabled={saving} onClick={() => onUndo(user)}>
              <RotateCcw size={14} /> 최근 작업 되돌리기
            </button>
            <button type="button" style={primaryButton(saving)} disabled={saving || user.resign_date} onClick={() => onRenew({ user_id: user.user_id, name: user.name, company_code: user.company_code, enrollment })}>
              현재 계약 갱신
            </button>
            <button type="button" style={dangerButton(saving)} disabled={saving || user.resign_date} onClick={() => onEnd({ user_id: user.user_id, name: user.name, company_code: user.company_code, enrollment })}>
              현재 계약 종료
            </button>
          </div>
          <div style={{ color: 'var(--text-sub)', fontSize: 12, fontWeight: 750 }}>
            종료 처리는 계약갱신관리 이력만 남깁니다. 실제 퇴사일은 직원관리에서 별도로 등록해야 합니다.
          </div>
          {terms.map((term) => (
            <div key={term.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--bg)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                <Info label="구간" value={term.term_label} />
                <Info label="시작일" value={term.start_date || '-'} />
                <Info label="종료일" value={term.end_date || '-'} />
                <Info label="수습" value={probationText(term) || '-'} />
                <Info label="결정" value={`${term.decision_label}${term.decision_date ? ` · ${term.decision_date}` : ''}`} />
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <textarea
                  value={memoDrafts[term.id] ?? term.memo ?? ''}
                  onChange={(event) => onMemo(term.id, event.target.value)}
                  placeholder="비고"
                  style={{
                    flex: 1,
                    minHeight: 54,
                    border: '1px solid var(--border)',
                    borderRadius: 7,
                    background: 'var(--bg-input, var(--bg-card))',
                    color: 'var(--text)',
                    padding: 8,
                    resize: 'vertical',
                  }}
                />
                <button type="button" style={buttonBase} disabled={saving} onClick={() => onSaveMemo(term.id)}>
                  저장
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
